'use client';

import isHotkey from 'is-hotkey';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Brush,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Quote,
  Underline,
} from 'lucide-react';
import { CSSProperties, ReactNode, useCallback, useMemo } from 'react';
import {
  BaseEditor,
  createEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Transforms,
} from 'slate';
import { HistoryEditor, withHistory } from 'slate-history';
import {
  Editable,
  ReactEditor,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  useSlate,
  withReact,
} from 'slate-react';
import { CustomEditor, CustomElement } from './internals/custom-types';
import {
  AddLinkButton,
  LinkComponent,
  RemoveLinkButton,
  withInlines,
} from './internals/link-support';
import { DividerButton } from './internals/divider-support';
import { TableButton, withTables } from './internals/table-support';

const HOTKEYS: Record<string, string> = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

export function RichtextEditor({
  initialValues,
  onChange,
  readOnly = false,
  onBlur,
}: {
  initialValues: Descendant[];
  onChange?: (value: Descendant[]) => void;
  readOnly?: boolean;
  onBlur?: () => void;
}) {
  const renderElement = useCallback(
    ({ children, ...props }: RenderElementProps) => <Element {...props}>{children}</Element>,
    [],
  );
  const renderLeaf = useCallback(
    ({ children, ...props }: RenderLeafProps) => <Leaf {...props}>{children}</Leaf>,
    [],
  );
  const editor = useMemo(
    () => withCustomElements(withTables(withInlines(withHistory(withReact(createEditor()))))),
    [],
  );

  return (
    <Slate editor={editor} initialValue={initialValues} onChange={onChange}>
      {!readOnly && (
        <div className="mb-4 flex flex-wrap gap-3">
          <MarkButton format="bold" icon={<Bold />} />
          <MarkButton format="italic" icon={<Italic />} />
          <MarkButton format="underline" icon={<Underline />} />
          <MarkButton format="code" icon={<Code />} />
          <MarkButton format="primary" icon={<Brush />} />
          <DividerButton />
          <BlockButton format="heading" icon={<Heading1 />} />
          <BlockButton format="heading-two" icon={<Heading2 />} />
          <BlockButton format="heading-three" icon={<Heading3 />} />
          <BlockButton format="block-quote" icon={<Quote />} />
          <AddLinkButton />
          <RemoveLinkButton />
          <BlockButton format="numbered-list" icon={<ListOrdered />} />
          <BlockButton format="bulleted-list" icon={<List />} />
          <BlockButton format="left" icon={<AlignLeft />} />
          <BlockButton format="center" icon={<AlignCenter />} />
          <BlockButton format="right" icon={<AlignRight />} />
          <BlockButton format="justify" icon={<AlignJustify />} />
          <TableButton />
        </div>
      )}
      <Editable
        readOnly={readOnly}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        placeholder={!readOnly ? 'Enter some rich text…' : ''}
        className="outline-hidden"
        spellCheck
        onBlur={onBlur}
        onKeyDown={(event) => {
          for (const hotkey in HOTKEYS) {
            if (isHotkey(hotkey, event as any)) {
              event.preventDefault();
              const mark = HOTKEYS[hotkey];
              toggleMark(editor, mark as string);
            }
          }
        }}
      />
    </Slate>
  );
}

const toggleBlock = (editor: any, format: any) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

interface ToggleMarkEditor extends BaseEditor, ReactEditor, HistoryEditor {}

const toggleMark = (editor: ToggleMarkEditor, format: string) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor: CustomEditor, format: string, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType as keyof typeof n] === format,
    }),
  );

  return !!match;
};

const isMarkActive = (editor: CustomEditor, format: string) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format as keyof typeof marks] === true : false;
};

const withCustomElements = (editor: Editor) => {
  const { isVoid } = editor;

  editor.isVoid = (element) => {
    return element.type === 'divider' ? true : isVoid(element);
  };

  return editor;
};

const Element = ({ attributes, children, element }: RenderElementProps) => {
  const style: CSSProperties = { textAlign: (element as any).align };
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote
          className="border-primary my-5 ml-3 border-l pl-3 italic"
          style={style}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case 'divider':
      return <div className="bg-base-content/70 my-2 h-px w-full">{children}</div>;
    case 'bulleted-list':
      return (
        <ul className="ml-4 list-disc" style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'heading-three':
      return (
        <h3 style={style} {...attributes}>
          {children}
        </h3>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'link':
      return (
        <LinkComponent attributes={attributes} element={element} style={style}>
          {children}
        </LinkComponent>
      );
    case 'table':
      return (
        <table>
          <tbody {...attributes}>{children}</tbody>
        </table>
      );
    case 'table-row':
      return <tr {...attributes}>{children}</tr>;
    case 'table-cell':
      return (
        <td {...attributes} className="min-w-16">
          {children}
        </td>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.primary) {
    children = <span className="text-primary">{children}</span>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({
  format,
  icon,
}: {
  format: CustomElement['type'] | (typeof TEXT_ALIGN_TYPES)[number];
  icon: ReactNode;
}) => {
  const editor = useSlate();
  return (
    <button
      type="button"
      className={`btn btn-xs ${
        isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type')
          ? 'btn-primary'
          : ''
      }`}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      tabIndex={-1}
    >
      {icon}
    </button>
  );
};

const MarkButton = ({
  format,
  icon,
}: {
  format: 'bold' | 'italic' | 'underline' | 'code' | 'primary';
  icon: ReactNode;
}) => {
  const editor = useSlate();
  return (
    <button
      type="button"
      className={`btn btn-xs ${isMarkActive(editor, format) ? 'btn-primary' : ''}`}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      tabIndex={-1}
    >
      {icon}
    </button>
  );
};
